import { useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2, Sparkles, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { usePortfolioData } from "@/hooks/use-portfolio-data";
import {
  assignMaintenanceContractor,
  createExpense,
  createMaintenanceRequest,
  createReminder,
  createRentPayment,
  updateMaintenanceRequest,
  updateMaintenanceStatus,
} from "@/services/dataService";
import { parseQuickEntry, splitEntries } from "@/lib/quickEntry";
import { EXPENSE_CATEGORY_OPTIONS } from "@/lib/categories";
import { dayToTimestamp, houseName, tenantName } from "@/lib/ledger";

const KINDS = [
  { value: "repair", label: "Repair — done" },
  { value: "repairRequest", label: "Repair — needs doing" },
  { value: "expense", label: "Expense" },
  { value: "rent", label: "Rent received" },
  { value: "todo", label: "To-do / reminder" },
];
const EXAMPLES = [
  "Today we repaired the bathroom sink, cost 500, by Cesar at Dunran",
  "Watson paid rent",
  "Kitchen faucet leaking at Guilford",
  "HOA 75.39 Mayhill",
  "Need to check the gutters at Guilford tomorrow",
];
const field = "h-9 w-full rounded-lg border border-input bg-background px-2.5 text-sm";

function contractorLabel(c) {
  return c.contactName && c.companyName && c.contactName !== c.companyName ? `${c.contactName} (${c.companyName})` : c.companyName || c.contactName;
}

function todayKey() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

function summary(draft, ctx) {
  const parts = [];
  const house = ctx.propertiesById.get(draft.propertyId);
  const where = house ? ` at ${houseName(house)}` : "";
  if (draft.kind === "rent") parts.push(`Ledger: +$${draft.amount || "?"} rent${where}`);
  if (draft.kind === "expense" || (draft.kind === "repair" && draft.amount)) {
    parts.push(`Ledger: −$${draft.amount || "?"} ${draft.kind === "repair" ? "Repair & maintenance" : ""}${where}`.replace("  ", " "));
  }
  const when = new Date(`${draft.day}T12:00`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  if (draft.kind === "repair") parts.push(`Maintenance: logged as done${where}`);
  if (draft.kind === "repairRequest") {
    parts.push(draft.day > todayKey() ? `Maintenance: scheduled for ${when}${where}` : `Maintenance: new open job${where}`);
  }
  if (draft.kind === "todo") parts.push(`To-do for ${when}${where} — shows on Home under Coming up`);
  return parts;
}

/**
 * "Tell me what happened" box. Type one or more lines in plain English, check
 * the preview, press Save — each entry lands in the Ledger, Maintenance or
 * rent records of the right house.
 */
export default function QuickEntry({ autoFocus = false, onDone }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data } = usePortfolioData(["properties", "contractors", "tenants"]);
  const [text, setText] = useState("");
  const [drafts, setDrafts] = useState([]);
  const [saving, setSaving] = useState(false);

  const propertiesById = new Map(data.properties.map((p) => [String(p._id), p]));
  const contractorsById = new Map(data.contractors.map((c) => [String(c._id), c]));
  const activeTenants = data.tenants.filter((t) => t.isActive !== false);

  function understand(event) {
    event?.preventDefault();
    const lines = splitEntries(text);
    if (!lines.length) return;
    setDrafts(lines.map((line, index) => ({ id: `${Date.now()}-${index}`, text: line, ...parseQuickEntry(line, data) })));
  }

  const edit = (id, key, value) => setDrafts((prev) => prev.map((d) => (d.id === id ? { ...d, [key]: value } : d)));
  const problems = (d) => {
    const list = [];
    if (!d.description.trim()) list.push("description");
    if (!["expense", "todo"].includes(d.kind) && !d.propertyId) list.push("house");
    if (["repair", "expense", "rent"].includes(d.kind) && !(Number.parseFloat(d.amount) > 0) && d.kind !== "repair") list.push("amount");
    if (!dayToTimestamp(d.day)) list.push("date");
    return list;
  };

  async function saveOne(d) {
    const amount = Number.parseFloat(String(d.amount).replace(/[$,\s]/g, "")) || 0;
    const date = dayToTimestamp(d.day);
    const contractor = d.contractorId ? contractorsById.get(d.contractorId) : null;
    const places = new Set();
    if (d.kind === "repair" || d.kind === "repairRequest") {
      const job = await createMaintenanceRequest({
        propertyId: d.propertyId,
        title: d.description.trim(),
        descriptionText: d.text,
        category: contractor?.specialty?.[0] || "General",
        priority: "normal",
      });
      const jobId = job?._id || job?.id;
      if (contractor && jobId) await assignMaintenanceContractor({ requestId: jobId, contractorId: d.contractorId });
      if (jobId && d.kind === "repair") {
        await updateMaintenanceStatus({ id: jobId, status: "completed" });
        if (amount > 0) await updateMaintenanceRequest({ id: jobId, actualCost: amount });
      } else if (jobId) {
        const scheduled = d.day > todayKey();
        if (amount > 0 || scheduled) {
          await updateMaintenanceRequest({
            id: jobId,
            ...(amount > 0 ? { estimatedCost: amount } : {}),
            ...(scheduled ? { scheduledDate: date } : {}),
          });
        }
        if (scheduled) await updateMaintenanceStatus({ id: jobId, status: "scheduled" });
      }
      places.add("Maintenance");
    }
    if (d.kind === "todo") {
      await createReminder({
        propertyId: d.propertyId || undefined,
        title: d.description.trim(),
        notes: d.text,
        kind: "custom",
        dueAt: date,
      });
      places.add("Home · Coming up");
    }
    if ((d.kind === "repair" && amount > 0) || d.kind === "expense") {
      await createExpense({
        propertyId: d.propertyId || undefined,
        title: d.description.trim(),
        amount,
        category: d.kind === "repair" ? "maintenance" : d.category === "rent" ? "other" : d.category,
        date,
        isRecurring: false,
        notes: contractor ? contractorLabel(contractor) : undefined,
      });
      places.add("Ledger");
    }
    if (d.kind === "rent") {
      await createRentPayment({
        propertyId: d.propertyId,
        tenantId: d.tenantId || undefined,
        amount,
        paymentDate: date,
        status: "completed",
        notes: d.description.trim(),
      });
      places.add("Ledger");
    }
    return places;
  }

  async function saveAll() {
    const ready = drafts.filter((d) => !problems(d).length);
    if (!ready.length) return;
    setSaving(true);
    const places = new Set();
    const failed = [];
    for (const d of ready) {
      try {
        for (const place of await saveOne(d)) places.add(place);
      } catch (error) {
        failed.push({ d, error });
      }
    }
    setSaving(false);
    await queryClient.invalidateQueries({ queryKey: ["portfolio"] });
    const savedIds = new Set(ready.filter((d) => !failed.some((f) => f.d === d)).map((d) => d.id));
    setDrafts((prev) => prev.filter((d) => !savedIds.has(d.id)));
    if (savedIds.size) {
      toast({ title: `Saved ${savedIds.size} ${savedIds.size === 1 ? "entry" : "entries"}`, description: `Updated: ${[...places].join(" & ")}` });
      if (!failed.length && drafts.length === ready.length) setText("");
      onDone?.();
    }
    if (failed.length) {
      toast({ title: "Some entries didn't save", description: failed[0].error.message, variant: "destructive" });
    }
  }

  return (
    <div className="space-y-3">
      <form onSubmit={understand} className="space-y-2">
        <div className="relative">
          <Sparkles className="absolute left-3 top-3 h-4 w-4 text-primary" />
          <textarea
            autoFocus={autoFocus}
            rows={2}
            value={text}
            onChange={(event) => setText(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) understand(event);
            }}
            placeholder="Tell me what happened — e.g. “today we repaired the bathroom sink, cost 500, by Cesar at Dunran”"
            className="w-full resize-none rounded-xl border border-input bg-background py-2.5 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            aria-label="Describe what happened"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button type="submit" size="sm" disabled={!text.trim()}>
            Continue
          </Button>
          <span className="text-xs text-muted-foreground">Enter to continue · Shift+Enter for a new line (one entry per line)</span>
        </div>
        {!drafts.length && !text ? (
          <div className="flex flex-wrap gap-1.5">
            {EXAMPLES.map((example) => (
              <button
                key={example}
                type="button"
                onClick={() => setText(example)}
                className="rounded-full border px-2.5 py-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                {example}
              </button>
            ))}
          </div>
        ) : null}
      </form>

      {drafts.map((d) => {
        const issues = problems(d);
        return (
          <div key={d.id} className="rounded-xl border bg-background p-3">
            <div className="mb-2 flex items-start justify-between gap-2">
              <p className="text-sm text-muted-foreground">“{d.text}”</p>
              <button
                type="button"
                onClick={() => setDrafts((prev) => prev.filter((x) => x.id !== d.id))}
                className="text-muted-foreground hover:text-foreground"
                aria-label="Remove this entry"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-[170px_1fr_150px_120px_1fr]">
              <select className={field} value={d.kind} onChange={(e) => edit(d.id, "kind", e.target.value)} aria-label="Type">
                {KINDS.map((k) => (
                  <option key={k.value} value={k.value}>
                    {k.label}
                  </option>
                ))}
              </select>
              <input className={field} value={d.description} onChange={(e) => edit(d.id, "description", e.target.value)} aria-label="Description" />
              <select
                className={`${field} ${issues.includes("house") ? "border-amber-500" : ""}`}
                value={d.propertyId}
                onChange={(e) => edit(d.id, "propertyId", e.target.value)}
                aria-label="House"
              >
                <option value="">{d.kind === "expense" ? "Business (no house)" : d.kind === "todo" ? "Any house" : "Which house?"}</option>
                {data.properties.map((p) => (
                  <option key={p._id} value={String(p._id)}>
                    {houseName(p)}
                  </option>
                ))}
              </select>
              <input
                className={`${field} text-right ${issues.includes("amount") ? "border-amber-500" : ""}`}
                inputMode="decimal"
                placeholder="Amount"
                value={d.amount}
                onChange={(e) => edit(d.id, "amount", e.target.value)}
                aria-label="Amount"
              />
              <div className="grid grid-cols-2 gap-2">
                <input className={field} type="date" value={d.day} onChange={(e) => edit(d.id, "day", e.target.value)} aria-label="Date" />
                {d.kind === "rent" ? (
                  <select className={field} value={d.tenantId} onChange={(e) => edit(d.id, "tenantId", e.target.value)} aria-label="Tenant">
                    <option value="">Tenant…</option>
                    {activeTenants
                      .filter((t) => !d.propertyId || String(t.propertyId) === d.propertyId)
                      .map((t) => (
                        <option key={t._id} value={String(t._id)}>
                          {tenantName(t)}
                        </option>
                      ))}
                  </select>
                ) : d.kind === "todo" ? (
                  <span className="self-center text-xs text-muted-foreground">Due date ←</span>
                ) : d.kind === "expense" ? (
                  <select className={field} value={d.category} onChange={(e) => edit(d.id, "category", e.target.value)} aria-label="Category">
                    {EXPENSE_CATEGORY_OPTIONS.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <select className={field} value={d.contractorId} onChange={(e) => edit(d.id, "contractorId", e.target.value)} aria-label="Contractor">
                    <option value="">Contractor…</option>
                    {data.contractors.map((c) => (
                      <option key={c._id} value={String(c._id)}>
                        {contractorLabel(c)}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              {issues.length ? (
                <span className="text-amber-700 dark:text-amber-300">Add the {issues.join(", ")} to save this.</span>
              ) : (
                <>Will save → {summary(d, { propertiesById }).join(" · ")}</>
              )}
              {d.notes.filter((n) => !/Which house|No amount/.test(n)).map((n) => (
                <span key={n}> · {n}</span>
              ))}
            </p>
          </div>
        );
      })}

      {drafts.length ? (
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={saveAll} disabled={saving || !drafts.some((d) => !problems(d).length)} className="gap-1.5">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Save {drafts.length > 1 ? `${drafts.filter((d) => !problems(d).length).length} entries` : ""}
          </Button>
          <Button variant="ghost" onClick={() => setDrafts([])}>
            Cancel
          </Button>
          <span className="text-xs text-muted-foreground">
            Saved entries appear in the <Link to="/ledger" className="underline">Ledger</Link> and{" "}
            <Link to="/maintenance" className="underline">Maintenance</Link>.
          </span>
        </div>
      ) : null}
    </div>
  );
}
